import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../lib/i18n";

const Wordmark = ({ dark }) => (
  <Link to="/" data-testid="logo-home" aria-label="АРГАЛИ — главная" className="flex items-center gap-2.5 group">
    <img src="/brand/argali_emblem_ru_v2.png" alt="" aria-hidden="true" width="543" height="501" className="h-9 md:h-10 w-auto" />
    <span
      className="font-serif text-2xl tracking-[0.22em] font-semibold"
      style={{ color: dark ? "#11110F" : "#F4F0E7" }}
    >
      АРГАЛИ
    </span>
  </Link>
);

const navItems = (t) => [
  { to: "/excursions", label: t("nav_excursions") },
  { to: "/seasons", label: t("nav_seasons") },
  { to: "/map", label: t("nav_map") },
  { to: "/altai", label: t("nav_altai") },
  { to: "/guide", label: t("nav_guide") },
  { to: "/reviews", label: t("nav_reviews") },
  { to: "/contacts", label: t("nav_contacts") },
];

export const Header = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // pages with light background need a dark header from the start
  const lightPage = ["/excursions", "/map", "/credits", "/faq", "/places", "/reviews", "/contacts", "/privacy", "/personal-data-consent", "/admin", "/login", "/custom-tour"].some(
    (p) => location.pathname.startsWith(p)
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || lightPage;
  const dark = solid;

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: solid ? "rgba(244,240,231,0.92)" : "transparent",
          backdropFilter: solid ? "blur(14px)" : "none",
          borderBottom: solid ? "1px solid rgba(52,33,22,0.12)" : "1px solid transparent",
        }}
      >
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
          <Wordmark dark={dark} />

          <nav className="hidden lg:flex items-center gap-7">
            {navItems(t).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace(/\//g, "-")}`}
                className="text-[0.7rem] font-medium uppercase tracking-[0.16em] transition-colors duration-300 hover:text-argali-bronze"
                style={{ color: dark ? "#342116" : "#F4F0E7" }}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-1 text-[0.72rem] uppercase tracking-[0.15em]" style={{ color: dark ? "#342116" : "#F4F0E7" }}>
              {["ru", "en"].map((l, i) => (
                <React.Fragment key={l}>
                  <button
                    data-testid={`lang-${l}`}
                    onClick={() => setLang(l)}
                    className="transition-opacity"
                    style={{ opacity: lang === l ? 1 : 0.45, fontWeight: lang === l ? 700 : 400 }}
                  >
                    {l.toUpperCase()}
                  </button>
                  {i === 0 && <span style={{ opacity: 0.35 }}>/</span>}
                </React.Fragment>
              ))}
            </div>
            <button
              data-testid="cta-header-choose"
              onClick={() => navigate("/excursions")}
              className="hidden md:inline-flex btn-bronze !py-2.5 !px-5 !text-[0.68rem]"
            >
              {t("cta_choose_route")}
            </button>
            <button
              data-testid="mobile-menu-open"
              className="lg:hidden"
              onClick={() => setOpen(true)}
              aria-label="Menu"
            >
              <Menu size={26} color={dark ? "#342116" : "#F4F0E7"} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && <MobileMenu onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </>
  );
};

const MobileMenu = ({ onClose }) => {
  const { t, lang, setLang } = useLang();
  const items = [
    { to: "/excursions", label: t("nav_excursions") },
    { to: "/seasons", label: t("nav_seasons") },
    { to: "/map", label: t("nav_map") },
    { to: "/places", label: t("nav_places") },
    { to: "/guide", label: t("nav_guide") },
    { to: "/reviews", label: t("nav_reviews") },
    { to: "/contacts", label: t("nav_contacts") },
  ];
  return (
    <motion.div
      data-testid="mobile-menu"
      className="fixed inset-0 z-[60] grain"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{ background: "#11110F" }}
    >
      <div
        className="absolute inset-0 opacity-30 ken-burns"
        style={{ backgroundImage: "url(/photos/photo_katu.jpg)", backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,15,0.6), rgba(17,17,15,0.9))" }} />
      <div className="relative h-full flex flex-col px-7 pt-7 pb-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/brand/argali_emblem_ru_v2.png" alt="" aria-hidden="true" width="543" height="501" className="h-9 w-auto" />
            <span className="font-serif text-2xl tracking-[0.22em] text-argali-ivory">АРГАЛИ</span>
          </div>
          <button data-testid="mobile-menu-close" onClick={onClose}>
            <X size={28} color="#F4F0E7" />
          </button>
        </div>
        <nav className="flex-1 flex flex-col justify-center gap-1">
          {items.map((item, i) => (
            <motion.div
              key={item.to}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={item.to}
                onClick={onClose}
                className="font-serif text-4xl text-argali-ivory hover:text-argali-gold transition-colors block py-1.5"
              >
                {item.label}
              </Link>
            </motion.div>
          ))}
        </nav>
        <div className="flex items-center gap-5">
          <div className="flex gap-2 text-sm uppercase tracking-widest text-argali-ivory">
            {["ru", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} style={{ opacity: lang === l ? 1 : 0.45, fontWeight: lang === l ? 700 : 400 }}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <a href="https://t.me/argalialtai" target="_blank" rel="noreferrer" className="text-sm uppercase tracking-widest text-argali-gold">Telegram</a>
          <a href="https://www.instagram.com/argalialtai" target="_blank" rel="noreferrer" className="text-sm uppercase tracking-widest text-argali-gold">Instagram</a>
          <a href="tel:+79941102511" className="text-sm uppercase tracking-widest text-argali-gold">{lang === "ru" ? "Позвонить" : "Call"}</a>
        </div>
      </div>
    </motion.div>
  );
};

export default Header;